import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { ReactNode, useState, useRef, useCallback } from "react";
import { Pressable, StyleSheet, ViewStyle, StyleProp, View } from "react-native";
import { useToolTip } from "@/hooks/useToolTip";
import { ThemedText } from "@/components/ThemedText";

type StepperProps = {
  onStepLeft?: () => any;
  onJumpLeft?: () => void;
  onStepRight?: () => any;
  onJumpRight?: () => void;
  canStepLeft?: boolean;
  canStepRight?: boolean;
  center?: ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Tooltip for step left button */
  stepLeftTooltip?: string;
  /** Tooltip for jump left button */
  jumpLeftTooltip?: string;
  /** Tooltip for step right button */
  stepRightTooltip?: string;
  /** Tooltip for jump right button */
  jumpRightTooltip?: string;
};

export function Stepper({
  onStepLeft,
  onJumpLeft,
  onStepRight,
  onJumpRight,
  canStepLeft = true,
  canStepRight = true,
  center: children,
  style,
  stepLeftTooltip,
  jumpLeftTooltip,
  stepRightTooltip,
  jumpRightTooltip,
}: StepperProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();

  const [hoverJumpRight, setHoverJumpRight] = useState(false);
  const [hoverJumpLeft, setHoverJumpLeft] = useState(false);
  const [hoverLeft, setHoverLeft] = useState(false);
  const [hoverRight, setHoverRight] = useState(false);

  const jumpLeftRef = useRef<View>(null);
  const stepLeftRef = useRef<View>(null);
  const stepRightRef = useRef<View>(null);
  const jumpRightRef = useRef<View>(null);
  const isHovering = useRef(false);

  const showTooltip = useCallback(
    (text: string, ref: React.RefObject<View | null>, alignRight = false) => {
      isHovering.current = true;
      setTimeout(() => {
        if (!isHovering.current) return;
        toolTip.setToolTipContent(
          <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
            <ThemedText style={{ fontSize: 12 }}>{text}</ThemedText>
          </View>,
        );
        ref.current?.measureInWindow((x, y, width) => {
          if (!isHovering.current) return;
          // For right-aligned tooltips, position from the right edge of the element
          // Estimate tooltip width based on text length (roughly 7px per char + padding)
          const estimatedTooltipWidth = text.length * 7 + 16;
          const xPos = alignRight ? x + width - estimatedTooltipWidth : x;
          toolTip.setLocation({ x: xPos, y: y - 36 } as any);
          toolTip.setVisible(true);
        });
      }, 300);
    },
    [colors.background, colors.border, toolTip],
  );

  const hideTooltip = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <View style={[styles.container, style]}>
      {onJumpLeft && (
        <Pressable
          ref={jumpLeftRef}
          onHoverIn={() => {
            setHoverJumpLeft(true);
            if (jumpLeftTooltip) showTooltip(jumpLeftTooltip, jumpLeftRef);
          }}
          onHoverOut={() => {
            setHoverJumpLeft(false);
            hideTooltip();
          }}
          style={[
            styles.jumpButton,
            { borderColor: colors.border },
            canStepLeft && hoverJumpLeft && { backgroundColor: colors.border },
          ]}
          disabled={!canStepLeft}
          onPress={onJumpLeft}
        >
          <MaterialIcons
            size={canStepLeft ? 20 : 16}
            color={canStepLeft ? colors.text : colors.placeholder}
            style={styles.jumpIcon}
            name="chevron-left"
          />
          <MaterialIcons
            size={canStepLeft ? 20 : 16}
            color={canStepLeft ? colors.text : colors.placeholder}
            style={styles.jumpIcon}
            name="chevron-left"
          />
        </Pressable>
      )}
      <Pressable
        ref={stepLeftRef}
        onHoverIn={() => {
          setHoverLeft(true);
          if (stepLeftTooltip) showTooltip(stepLeftTooltip, stepLeftRef);
        }}
        onHoverOut={() => {
          setHoverLeft(false);
          hideTooltip();
        }}
        style={[
          styles.button,
          { borderColor: colors.border },
          canStepLeft && hoverLeft && { backgroundColor: colors.border },
        ]}
        disabled={!canStepLeft}
        onPress={onStepLeft}
      >
        <MaterialIcons
          size={canStepLeft ? 20 : 16}
          color={canStepLeft ? colors.text : colors.placeholder}
          name="chevron-left"
        />
      </Pressable>

      <View style={styles.content}>{children}</View>

      <Pressable
        ref={stepRightRef}
        onHoverIn={() => {
          setHoverRight(true);
          if (stepRightTooltip) showTooltip(stepRightTooltip, stepRightRef, true);
        }}
        onHoverOut={() => {
          setHoverRight(false);
          hideTooltip();
        }}
        style={[
          styles.button,
          { borderColor: colors.border },
          canStepRight && hoverRight && { backgroundColor: colors.border },
        ]}
        disabled={!canStepRight}
        onPress={onStepRight}
      >
        <MaterialIcons
          size={canStepRight ? 20 : 16}
          color={canStepRight ? colors.text : colors.placeholder}
          name="chevron-right"
        />
      </Pressable>
      {onJumpRight && (
        <Pressable
          ref={jumpRightRef}
          onHoverIn={() => {
            setHoverJumpRight(true);
            if (jumpRightTooltip) showTooltip(jumpRightTooltip, jumpRightRef, true);
          }}
          onHoverOut={() => {
            setHoverJumpRight(false);
            hideTooltip();
          }}
          style={[
            styles.jumpButton,
            { borderColor: colors.border },
            canStepRight && hoverJumpRight && { backgroundColor: colors.border },
          ]}
          disabled={!canStepRight}
          onPress={onJumpRight}
        >
          <MaterialIcons
            size={canStepRight ? 20 : 16}
            color={canStepRight ? colors.text : colors.placeholder}
            style={styles.jumpIcon}
            name="chevron-right"
          />
          <MaterialIcons
            size={canStepRight ? 20 : 16}
            color={canStepRight ? colors.text : colors.placeholder}
            style={styles.jumpIcon}
            name="chevron-right"
          />
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    borderRadius: 6,
    gap: 4,
    alignItems: "center",
  },
  jumpButton: {
    flexDirection: "row",
    height: 28,
    alignItems: "center",
    width: 28,
    justifyContent: "flex-start",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
  },
  button: {
    height: 28,
    alignItems: "center",
    width: 28,
    justifyContent: "center",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
  },
  jumpIcon: {
    maxWidth: 6,
    flex: 1,
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  tooltip: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
});
